import { rotaEditorDeFluxosNoNo } from '@/shared/constants/rotasFluxos';
import { isRecord } from '@/shared/utils/apiResponse';
import { pluralizar } from '@/shared/utils/text';

import type {
  ConflitoDeFinalidade,
  ConsultaDeTemplatesWhatsapp,
  ContaDoTemplateWhatsapp,
  FinalidadeWhatsapp,
  OpcaoDeFinalidadeWhatsapp,
  OpcaoDoFiltroDeFinalidade,
  OrigemDoUsoDeTemplate,
  SituacaoDoTemplateNaConta,
  TemplateWhatsappDaGestao,
  UsoDeTemplateWhatsapp,
  ValorDoFiltroDeFinalidade,
  VarianteDaSituacaoNaConta,
} from '../types/templatesWhatsappGestao';
import { tiposTelefoneTemplate } from './tipoTelefoneTemplate';

export const TEMPLATES_WHATSAPP_POR_PAGINA = 20;
export const USOS_DO_TEMPLATE_POR_PAGINA = 20;
export const TODAS_AS_FINALIDADES = 'TODAS';

/** Os rótulos são os do seletor de tipo do nó: a finalidade é o que esse seletor filtra. */
export const OPCOES_DE_FINALIDADE_WHATSAPP: OpcaoDeFinalidadeWhatsapp[] = [
  ...tiposTelefoneTemplate,
  { value: 'NOTIFICACAO', label: 'Notificação' },
];

export const OPCOES_DO_FILTRO_DE_FINALIDADE: OpcaoDoFiltroDeFinalidade[] = [
  { value: TODAS_AS_FINALIDADES, label: 'Todas' },
  ...OPCOES_DE_FINALIDADE_WHATSAPP,
  { value: 'SEM_FINALIDADE', label: 'Sem finalidade' },
];

const SITUACAO_NA_CONTA: Record<
  SituacaoDoTemplateNaConta,
  { rotulo: string; variante: VarianteDaSituacaoNaConta }
> = {
  APROVADO: { rotulo: 'Aprovado', variante: 'success' },
  PENDENTE: { rotulo: 'Pendente', variante: 'warning' },
  ENVIANDO: { rotulo: 'Enviando', variante: 'info' },
  RECUSADO: { rotulo: 'Recusado', variante: 'error' },
  SUSPENSO: { rotulo: 'Suspenso', variante: 'warning' },
  ERRO: { rotulo: 'Erro', variante: 'error' },
  AUSENTE: { rotulo: 'Não enviado', variante: 'neutral' },
};

const ROTULO_DA_ORIGEM_DO_USO: Record<OrigemDoUsoDeTemplate, string> = {
  RASCUNHO: 'Rascunho',
  PUBLICADO: 'Publicado',
  FIXADO: 'Versão fixada',
};

/** Contas que o "Reenviar à conta" alcança: o core só redistribui as sem relação ou com erro. */
const SITUACOES_A_REENVIAR: ReadonlySet<SituacaoDoTemplateNaConta> = new Set(['AUSENTE', 'ERRO']);

export function ehFinalidadeWhatsapp(valor: unknown): valor is FinalidadeWhatsapp {
  return OPCOES_DE_FINALIDADE_WHATSAPP.some((opcao) => opcao.value === valor);
}

export function ehValorDoFiltroDeFinalidade(valor: string): valor is ValorDoFiltroDeFinalidade {
  return OPCOES_DO_FILTRO_DE_FINALIDADE.some((opcao) => opcao.value === valor);
}

/** Aceita qualquer tipo de linha: o core pode devolver tipo que não é finalidade de template. */
export function rotuloDaFinalidadeWhatsapp(finalidade: string | null): string {
  if (!finalidade) return 'Sem finalidade';
  return (
    OPCOES_DE_FINALIDADE_WHATSAPP.find((opcao) => opcao.value === finalidade)?.label ?? finalidade
  );
}

/** Só o tipo: o envio por tipo usa todas as linhas dele, então o número de origem não importa. */
export function rotuloDaLinhaDeOrigem(template: TemplateWhatsappDaGestao): string {
  const { linhaDeOrigem } = template;
  if (!linhaDeOrigem) return 'Sem linha de origem';
  return rotuloDaFinalidadeWhatsapp(linhaDeOrigem.tipo);
}

export function rotuloDaContaDoTemplate(conta: ContaDoTemplateWhatsapp): string {
  const legado = conta.via === 'LEGADO' ? ' (legado)' : '';
  return `…${conta.wabaFinal} · ${SITUACAO_NA_CONTA[conta.situacao].rotulo}${legado}`;
}

export function varianteDaContaDoTemplate(
  conta: ContaDoTemplateWhatsapp,
): VarianteDaSituacaoNaConta {
  return SITUACAO_NA_CONTA[conta.situacao].variante;
}

export function descricaoDoUsoDoTemplate(uso: UsoDeTemplateWhatsapp): string {
  const partes = [`Nó ${uso.nodeLabel}`, ROTULO_DA_ORIGEM_DO_USO[uso.origem]];
  if (uso.tipoTelefone) partes.push(`envia por ${rotuloDaFinalidadeWhatsapp(uso.tipoTelefone)}`);
  if (uso.execucoes > 0)
    partes.push(pluralizar(uso.execucoes, 'execução em andamento', 'execuções em andamento'));
  return partes.join(' · ');
}

/**
 * Sem finalidade o core não tem para onde distribuir, então o reenvio só aparece depois que ela
 * existe e alguma conta ficou sem o template ou com erro.
 */
export function precisaReenviarAsContas(template: TemplateWhatsappDaGestao): boolean {
  return (
    template.finalidadeWhatsapp !== null &&
    template.contas.some((conta) => SITUACOES_A_REENVIAR.has(conta.situacao))
  );
}

/** Fluxo de agente abre no detalhe do agente; fluxo arbitral, sem agente, abre no editor já no nó. */
export function linkDoUsoDoTemplate(uso: UsoDeTemplateWhatsapp): string {
  return uso.agenteId
    ? `/admin-geral/flows/agents/${uso.agenteId}`
    : rotaEditorDeFluxosNoNo(uso.flowId, uso.nodeId);
}

export function chaveDoUsoDoTemplate(uso: UsoDeTemplateWhatsapp): string {
  return `${uso.origem}:${uso.flowVersionId ?? uso.flowId}:${uso.nodeId}`;
}

export function consultaDosTemplatesWhatsapp(
  page: number,
  busca: string,
  filtro: ValorDoFiltroDeFinalidade,
): ConsultaDeTemplatesWhatsapp {
  const termo = busca.trim();
  return {
    page,
    limit: TEMPLATES_WHATSAPP_POR_PAGINA,
    ...(termo ? { search: termo } : {}),
    ...(filtro !== TODAS_AS_FINALIDADES ? { finalidade: filtro } : {}),
  };
}

const ORIGENS_DO_USO: ReadonlySet<unknown> = new Set<OrigemDoUsoDeTemplate>([
  'RASCUNHO',
  'PUBLICADO',
  'FIXADO',
]);

const textoOuNulo = (valor: unknown): boolean => valor === null || typeof valor === 'string';

function ehUsoDeTemplateWhatsapp(valor: unknown): valor is UsoDeTemplateWhatsapp {
  return (
    isRecord(valor) &&
    ORIGENS_DO_USO.has(valor.origem) &&
    typeof valor.flowId === 'string' &&
    typeof valor.flowNome === 'string' &&
    textoOuNulo(valor.agenteId) &&
    textoOuNulo(valor.agenteNome) &&
    textoOuNulo(valor.flowVersionId) &&
    typeof valor.nodeId === 'string' &&
    typeof valor.nodeLabel === 'string' &&
    textoOuNulo(valor.tipoTelefone) &&
    typeof valor.execucoes === 'number'
  );
}

/**
 * Lê o `details` do 409 `TEMPLATE_FINALIDADE_EM_USO`. Um item fora do contrato invalida o
 * conjunto: listar só parte dos usos faria a pessoa achar que já resolveu todos.
 */
export function conflitoDeFinalidadeEmDetalhes(detalhes: unknown): ConflitoDeFinalidade | null {
  if (
    !isRecord(detalhes) ||
    !Array.isArray(detalhes.itens) ||
    !detalhes.itens.every(ehUsoDeTemplateWhatsapp) ||
    typeof detalhes.total !== 'number' ||
    typeof detalhes.truncado !== 'boolean'
  )
    return null;
  return { usos: detalhes.itens, total: detalhes.total, truncado: detalhes.truncado };
}
